import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ArrowLeft, ArrowRight, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { PLANS, priceFor } from "@/constants/plans";
import { useStore } from "@/context/StoreContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const ease = [0.16, 1, 0.3, 1];
const EMPTY = { first_name: "", last_name: "", address: "", phone: "", email: "", gps_id: "" };

export const OrderModal = () => {
  const { orderOpen, setOrderOpen, selectedPlan } = useStore();
  const [step, setStep] = useState(0);
  const [planId, setPlanId] = useState(selectedPlan);
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (orderOpen) {
      setStep(0);
      setPlanId(selectedPlan);
      setOrderNumber(null);
    }
  }, [orderOpen, selectedPlan]);

  const plan = PLANS.find((p) => p.id === planId);
  const total = priceFor(plan);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const gpsValid = /^\d{10}$/.test(form.gps_id.trim());
  const detailsValid =
    form.first_name.trim() && form.last_name.trim() && form.address.trim() &&
    form.phone.trim() && /\S+@\S+\.\S+/.test(form.email) && gpsValid;

  const submitOrder = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        plan: planId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        gps_id: form.gps_id.trim(),
        terms_accepted: termsAccepted,
      });
      setOrderNumber(data.order_number);
      setStep(3);
      setForm(EMPTY);
      toast.success("Bestilling registrert!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const stepTitle = ["Velg periode", "Dine opplysninger", "Bekreft bestilling", "Takk!"];

  return (
    <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-xl bg-[#0F1623] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="order-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display text-xl font-bold">{stepTitle[step]}</DialogTitle>
            {step < 3 && (
              <span className="mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Steg {step + 1} / 3</span>
            )}
          </div>
          {step < 3 && (
            <div className="flex gap-1.5 pt-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-cyan-400" : "bg-white/10"}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -28 }}
            transition={{ duration: 0.4, ease }}
          >
            {step === 0 && (
              <div className="space-y-4 pt-2" data-testid="order-step-plan">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPlanId(p.id)}
                    data-testid={`order-select-plan-${p.id}`}
                    className={`w-full text-left p-4 rounded-2xl border transition-all duration-300 flex items-center justify-between ${
                      planId === p.id ? "border-cyan-400/60 bg-cyan-400/[0.07]" : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div>
                      <p className="font-display font-bold">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.tagline} · alt inkludert</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-display font-bold text-lg leading-tight">{p.total},-</p>
                        <p className="mono text-[10px] text-cyan-400/80">{p.perMonth},-/mnd</p>
                      </div>
                      <span className={`flex h-5 w-5 rounded-full border-2 items-center justify-center ${planId === p.id ? "border-cyan-400 bg-cyan-400" : "border-slate-600"}`}>
                        {planId === p.id && <Check size={12} className="text-[#06090E]" />}
                      </span>
                    </div>
                  </button>
                ))}

                <button onClick={() => setStep(1)} data-testid="order-next-button"
                  className="w-full font-display font-bold bg-cyan-400 hover:bg-cyan-300 text-[#06090E] py-3.5 rounded-full transition-colors flex items-center justify-center gap-2">
                  Fortsett <ArrowRight size={16} />
                </button>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 pt-2" data-testid="order-step-details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="o-first-name">Fornavn</Label>
                    <Input id="o-first-name" value={form.first_name} onChange={set("first_name")} data-testid="order-first-name-input" className="bg-[#06090E] border-white/10" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="o-last-name">Etternavn</Label>
                    <Input id="o-last-name" value={form.last_name} onChange={set("last_name")} data-testid="order-last-name-input" className="bg-[#06090E] border-white/10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="o-address">Adresse</Label>
                  <Input id="o-address" value={form.address} onChange={set("address")} data-testid="order-address-input" className="bg-[#06090E] border-white/10" placeholder="Gateadresse, postnummer og sted" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="o-phone">Telefonnummer</Label>
                    <Input id="o-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="order-phone-input" className="bg-[#06090E] border-white/10" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="o-email">E-postadresse</Label>
                    <Input id="o-email" type="email" value={form.email} onChange={set("email")} data-testid="order-email-input" className="bg-[#06090E] border-white/10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="o-gps-id">GPS-ID (10 siffer)</Label>
                  <Input
                    id="o-gps-id"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.gps_id}
                    onChange={(e) => setForm({ ...form, gps_id: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    data-testid="order-gps-id-input"
                    className="bg-[#06090E] border-white/10 mono tracking-[0.2em]"
                    placeholder="f.eks. 3512345678"
                  />
                  <p className="text-xs text-slate-500">Du får GPS-ID-en fra forhandleren eller i esken sammen med trackeren.
                    {form.gps_id.length > 0 && !gpsValid && (
                      <span className="text-amber-400"> Må være nøyaktig 10 siffer ({form.gps_id.length}/10).</span>
                    )}
                  </p>
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <button onClick={() => setStep(0)} data-testid="order-back-button"
                    className="sm:px-6 py-3.5 rounded-full border border-white/15 text-slate-300 hover:border-white/30 transition-colors flex items-center justify-center gap-2">
                    <ArrowLeft size={16} /> Tilbake
                  </button>
                  <button onClick={() => setStep(2)} disabled={!detailsValid} data-testid="order-next-button-2"
                    className="flex-1 font-display font-bold bg-cyan-400 hover:bg-cyan-300 text-[#06090E] py-3.5 rounded-full transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                    Til oppsummering <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5 pt-2" data-testid="order-step-summary">
                <div className="rounded-2xl border border-white/10 bg-[#06090E] p-5 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Abonnement</span><span className="font-semibold">{plan.name} – alt inkludert</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Navn</span><span>{form.first_name} {form.last_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">E-post</span><span>{form.email}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Telefon</span><span>{form.phone}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Adresse</span><span>{form.address}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">GPS-ID</span><span className="mono tracking-wider">{form.gps_id}</span></div>
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="font-display font-bold">Totalt</span>
                    <span className="font-display font-extrabold text-xl text-cyan-400" data-testid="order-total-price">
                      {total},- <span className="text-xs font-normal text-slate-500">({plan.perMonth},-/mnd)</span>
                    </span>
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer rounded-2xl border border-white/10 bg-[#06090E] p-4 hover:border-cyan-400/30 transition-colors" data-testid="order-terms-wrapper">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    data-testid="order-terms-checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded accent-cyan-400 cursor-pointer"
                  />
                  <span className="text-xs text-slate-400 leading-relaxed">
                    Jeg har lest og godtar{" "}
                    <a href="/vilkar" target="_blank" rel="noopener noreferrer" data-testid="order-terms-link" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                      vilkårene og betingelsene
                    </a>{" "}
                    til CLSF AS, og bekrefter at jeg er over 18 år.
                  </span>
                </label>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Ved å sende bestillingen godtar du at Apexa Link kontakter deg. Betaling skjer via eFaktura
                  fra vårt regnskapsprogram – ingen kortopplysninger tastes inn her. Abonnementet løper i valgt periode.
                </p>
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <button onClick={() => setStep(1)} data-testid="order-back-button-2"
                    className="sm:px-6 py-3.5 rounded-full border border-white/15 text-slate-300 hover:border-white/30 transition-colors flex items-center justify-center gap-2">
                    <ArrowLeft size={16} /> Tilbake
                  </button>
                  <button onClick={submitOrder} disabled={loading || !termsAccepted} data-testid="submit-order-form"
                    className="flex-1 font-display font-bold bg-[#FF4800] hover:bg-[#ff5d1f] text-white py-3.5 rounded-full transition-all disabled:opacity-50 hover:shadow-[0_0_28px_rgba(255,72,0,0.4)]">
                    {loading ? "Sender..." : "Send bestilling"}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="text-center py-8" data-testid="order-success">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-400/10 border border-emerald-400/40"
                >
                  <PartyPopper size={34} className="text-emerald-400" />
                </motion.div>
                <h3 className="font-display font-extrabold text-2xl mb-3">Bestillingen er registrert!</h3>
                {orderNumber && (
                  <div className="inline-block mono text-sm tracking-[0.2em] text-cyan-300 border border-cyan-400/30 bg-cyan-400/[0.07] rounded-full px-6 py-2.5 mb-5" data-testid="order-number">
                    {orderNumber}
                  </div>
                )}
                <p className="text-slate-400 text-sm max-w-sm mx-auto leading-relaxed mb-8">
                  Takk for bestillingen! Du mottar ordrebekreftelse med ordrenummer på e-posten din,
                  og eFaktura sendes fra vårt regnskapsprogram.
                </p>
                <button onClick={() => setOrderOpen(false)} data-testid="order-close-button"
                  className="font-display font-bold bg-cyan-400 hover:bg-cyan-300 text-[#06090E] px-10 py-3.5 rounded-full transition-colors">
                  Ferdig
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
